import { isCI } from '@servicetitan/install';
import { execSync } from 'child_process';
import path from 'path';
import { getTsConfig, isWebComponent, log, logErrors, readJson } from '../../utils';
import { TSConfig } from '../utils';
import type { entry } from './registry/upload-sourcemaps';
import { Command } from './types';

interface PackageJson {
    name: string;
}

export class UploadSourcemaps extends Command<typeof entry> {
    #outDir?: string;
    #packageJson?: PackageJson;

    @logErrors
    // eslint-disable-next-line @typescript-eslint/require-await
    async execute() {
        this.checkLocation();
        if (!this.checkArgs()) {
            return;
        }

        const bundleDir = this.getBundleDir();
        const options = this.getOptions().join(' ');
        this.run(`npx --yes @datadog/datadog-ci@3 sourcemaps upload ${bundleDir} ${options}`);
    }

    private checkArgs() {
        if (!process.env.DATADOG_API_KEY) {
            const message = 'DATADOG_API_KEY environment variable is not set';
            if (!isCI()) {
                throw new Error(message);
            }
            log.warning(`${message}; skipping sourcemaps`);
            return false;
        }

        return true;
    }

    private checkLocation() {
        if (!isWebComponent()) {
            throw new Error(`this command must be run inside a web-component directory`);
        }
    }

    private getBundleDir() {
        return path.relative('.', path.join(this.getOutDir(), 'bundle'));
    }

    private getMinifiedPrefixPath() {
        const { name } = this.getPackageJson();
        const baseUrl = `https://unpkg.servicetitan.com/${name}@${this.args.releaseVersion}`;
        return `${baseUrl}/${this.getBundleDir().replace(/\\/g, '/')}`;
    }

    private getOptions() {
        const { dry, releaseVersion } = this.args;
        const service = this.getPackageJson().name.replace(/^[^/]+\//, '');
        return [
            ...[dry && `--dry-run`],
            `--service=${service}`,
            `--release-version=${releaseVersion}`,
            `--minified-path-prefix=${this.getMinifiedPrefixPath()}`,
            `--project-path=${this.getProjectPath()}`,
        ].filter(item => !!item) as string[];
    }

    private getOutDir() {
        if (!this.#outDir) {
            const outDir = new TSConfig(getTsConfig()).getValue<string>('compilerOptions.outDir');
            if (!outDir) {
                throw new Error('compilerOptions.outDir is not configured');
            }

            this.#outDir = outDir;
        }
        return this.#outDir;
    }

    private getPackageJson() {
        if (!this.#packageJson) {
            this.#packageJson = readJson<PackageJson>('package.json');
        }
        return this.#packageJson;
    }

    private getProjectPath() {
        const name = readJson(path.join(this.getOutDir(), 'metadata.json')).name;
        return `${name}/`;
    }

    private run(command: string) {
        log.info(`Running: ${command}`);
        return execSync(command, { stdio: 'inherit' });
    }
}
